import React, { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { SideDrawer, SideDrawerCloseButton } from "@dmc/ui";
import ApiData from "../../api/ApiData";
import RolesState from "../../api/RolesState";
import { ContainerSubTitleGrey } from "../ContainerSubTitleGrey";
import { DashCard } from "../UI/DashCard";
import { BtnPrimaryv4 } from "../UI/button/BtnPrimaryv4";
import { BtnOutlineV4 } from "../UI/button/BtnOutlineV4";
import RegionBtn from "./RegionBtn";
import { SiteModalToolbarBtn } from "./SiteModalToolbarBtn";
import Loader from "../Loader";
import { NoRightsMessage } from "../UI/NoRightsMessage";

const innLabelByCountry = (country) => {
  if (country === "BY") return "УНП";
  if (country === "KZ") return "БИН";
  return "ИНН";
};

const PartnerLinkedCompaniesPanel = observer(({ partnerCompanyId }) => {
  const linkModalTitleId = useId();
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");

  const linksLoaded = ApiData.partnerLinksCompanyId === partnerCompanyId;
  const links = linksLoaded ? ApiData.partnerLinks ?? [] : null;

  const activeLinks = (links ?? []).filter((l) => l.is_active !== false);

  const linkedClientIds = new Set(
    activeLinks.map((l) => l.company?.id).filter(Boolean),
  );

  const availableClients = useMemo(() => {
    const all = ApiData.companys ?? [];
    return all.filter(
      (c) => c.is_partner === false && !linkedClientIds.has(c.id),
    );
  }, [ApiData.companys, linkedClientIds]);

  const filteredClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableClients;
    return availableClients.filter(
      (c) =>
        c.name_company?.toLowerCase().includes(q) ||
        String(c.inn ?? "").toLowerCase().includes(q),
    );
  }, [availableClients, query]);

  const bindClient = async (clientId) => {
    const inactiveLink = (ApiData.partnerLinks ?? []).find(
      (l) => l.company?.id === clientId && l.is_active === false,
    );
    const ok = inactiveLink
      ? await ApiData.updatePartnerLink(inactiveLink.link_id, {
          is_active: true,
        })
      : await ApiData.createPartnerLink(partnerCompanyId, clientId);
    if (ok) {
      setModalOpen(false);
      setQuery("");
    }
  };

  const linkCount = activeLinks.length;
  const canManageLinks = RolesState.post_company_partner_link;

  return (
    <DashCard
      className="sites-panel partner-linked-panel production-container-card-company"
      title="Привязанные компании:"
      headerClassName="production-title-bar_container"
      headerActions={
        canManageLinks ? (
          <BtnPrimaryv4 onClick={() => setModalOpen(true)} />
        ) : null
      }
    >
      {links === null ? (
        <Loader />
      ) : ApiData.partnerLinksForbidden ? (
        <NoRightsMessage>
          Нет прав для просмотра привязанных компаний. Нужно разрешение
          «Получить партнёров».
        </NoRightsMessage>
      ) : activeLinks.length === 0 ? (
        <ContainerSubTitleGrey text="Компании не привязаны" />
      ) : (
        <div className="sites-list container-list-this">
          {activeLinks.map((link) => {
            const company = link.company;
            if (!company) return null;
            const country = company.country_id_rel?.tag_country ?? "RU";
            return (
              <article
                key={link.link_id}
                className="site-item site-item--partner-link"
              >
                <div className="site-item-header">
                  <span className="site-item-name">{company.name_company}</span>
                  <RegionBtn region={country} badgeV4 />
                </div>
                <div className="site-item-details">
                  <div className="site-detail-line">
                    <span className="detail-label">
                      {innLabelByCountry(country)}:
                    </span>
                    <span className="detail-value detail-value-mono">
                      {company.inn}
                    </span>
                  </div>
                </div>
                <div className="partner-linked-row-actions">
                  <Link
                    className="btn-outline-v4"
                    to={`/company/${company.inn}`}
                  >
                    Перейти
                  </Link>
                  {canManageLinks ? (
                    <BtnOutlineV4
                      text="Деактивировать"
                      onClick={() =>
                        ApiData.updatePartnerLink(link.link_id, {
                          is_active: false,
                        })
                      }
                    />
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {links !== null && !modalOpen ? (
        <div className="sites-panel-footer">Всего компаний: {linkCount}</div>
      ) : null}

      {modalOpen ? (
        <SideDrawer
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Привязать компанию"
          titleId={linkModalTitleId}
          usePortal={false}
          toolbar={
            <SiteModalToolbarBtn
              variant="close"
              onClick={() => setModalOpen(false)}
              title="Закрыть"
            />
          }
        >
            <div className="site-modal-body partner-link-modal-body">
              <label className="partner-link-search-field search_id_client_container cc-v4-soft-th-id">
                <span className="cc-v4-soft-th-label">Поиск</span>
                <input
                  type="search"
                  className="search_id_client cc-v4-soft-id-input"
                  placeholder="Поиск по названию или ИНН"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Поиск компании для привязки"
                />
              </label>
              <div className="partner-link-picker-scroll">
                {filteredClients.length === 0 ? (
                  <ContainerSubTitleGrey text="Нет доступных компаний для привязки" />
                ) : (
                  <ul className="partner-link-picker-list">
                    {filteredClients.map((client) => {
                      const country =
                        client.country_id_rel?.tag_country ?? "RU";
                      return (
                        <li
                          key={client.id}
                          className="partner-link-picker-row"
                        >
                          <div
                            className="partner-link-picker-name"
                            title={client.name_company}
                          >
                            {client.name_company}
                          </div>
                          <div className="partner-link-picker-meta">
                            <span className="partner-link-picker-inn">
                              {innLabelByCountry(country)}: {client.inn}
                            </span>
                            <div className="partner-link-picker-actions">
                              <Link
                                className="btn-primary-v4 sub-status-action-btn"
                                to={`/company/${client.inn}`}
                              >
                                Перейти
                              </Link>
                              {canManageLinks ? (
                                <BtnPrimaryv4
                                  text="Привязать"
                                  onClick={() => bindClient(client.id)}
                                />
                              ) : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
        </SideDrawer>
      ) : null}
    </DashCard>
  );
});

export default PartnerLinkedCompaniesPanel;
